import { Box, Typography } from '@mui/material';
import { protoIcon } from './ProtoIcon';
import React from 'react';

/**
 * Peças do kit mobile (`prototipos-mobile/assets/mobile.css`) reutilizadas pelas
 * telas do shell mobile: `.list`/`.item`, `.pill`, `.card`, `.sec-h`, `.group-label`.
 * Valores copiados 1:1 do CSS do protótipo (claro e escuro via theme.palette).
 */

const ChevronRight = protoIcon('right');

const WARNING_FG_LIGHT = '#b45309';

// `.item .ico.<tom>` — [fundo, cor do ícone]
export function toneColors(theme, tone) {
    const p = theme.palette;
    switch (tone) {
        case 'purple': return [p.secondary.bg, p.secondary.main];
        case 'green': return [p.success.bg, p.success.main];
        case 'amber': return [p.warning.bg, WARNING_FG_LIGHT];
        case 'red': return [p.error.bg, p.error.main];
        case 'gray': return [p.background.subtle, p.text.secondary];
        default: return [p.info.bg, p.primary.main];
    }
}

export const CARD_SHADOW = '0 1px 2px rgba(15,23,42,.06)';

// `.card`
export function KitCard({ children, sx, component, ...rest }) {
    return (
        <Box
            component={component}
            {...rest}
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '16px',
                boxShadow: CARD_SHADOW,
                color: 'text.primary',
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

// `.sec-h` — título de seção (h2 14.5/800) + link opcional (13/700, brand)
export function SecH({ title, linkLabel, onLink, first, sx }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', m: first ? '0 0 10px' : '22px 0 10px', ...sx }}>
            <Typography component="h2" sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4 }}>{title}</Typography>
            {linkLabel && (
                <Box component="button" type="button" onClick={onLink} sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', fontFamily: 'inherit', color: 'primary.main', fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>
                    {linkLabel}
                </Box>
            )}
        </Box>
    );
}

// `.group-label`
export function GroupLabel({ children, first }) {
    return (
        <Typography
            sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', p: first ? '2px 4px 8px' : '18px 4px 8px', lineHeight: 1.4 }}
        >
            {children}
        </Typography>
    );
}

// `.list`
export function KitList({ children, sx }) {
    return (
        <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '16px', overflow: 'hidden', boxShadow: CARD_SHADOW, ...sx }}>
            {children}
        </Box>
    );
}

// `.item` (+ `.ico`, `.t`, `.s`, `.end`, `.chev`)
export function KitItem({ icon: Icon, tone, title, sub, end, chevron, onClick, children, sx }) {
    const clickable = Boolean(onClick);
    return (
        <Box
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '13px 14px',
                minHeight: 60,
                width: '100%',
                boxSizing: 'border-box',
                border: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'text.primary',
                cursor: clickable ? 'pointer' : 'default',
                '&:last-child': { borderBottom: 0 },
                ...(clickable ? { '&:active': { backgroundColor: 'background.subtle' } } : {}),
                ...sx,
            }}
        >
            {Icon && (
                <Box
                    sx={(theme) => {
                        const [bg, fg] = toneColors(theme, tone);
                        return { width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bg, color: fg, flexShrink: 0 };
                    }}
                >
                    <Icon size={20} />
                </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Box>
                {sub !== undefined && sub !== null && (
                    <Box sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px', lineHeight: 1.4 }}>{sub}</Box>
                )}
                {children}
            </Box>
            {end && <Box sx={{ textAlign: 'right', flexShrink: 0 }}>{end}</Box>}
            {chevron && <Box sx={{ color: 'text.disabled', display: 'inline-flex', flexShrink: 0 }}><ChevronRight size={18} /></Box>}
        </Box>
    );
}

// `.pill` (+ variantes)
export function KitPill({ tone, children, sx }) {
    return (
        <Box
            component="span"
            sx={(theme) => {
                const p = theme.palette;
                const dark = p.mode === 'dark';
                let bg = p.background.subtle;
                let fg = p.text.secondary;
                if (tone === 'success') { bg = p.success.bg; fg = p.success.main; }
                else if (tone === 'warning') { bg = p.warning.bg; fg = dark ? p.warning.main : WARNING_FG_LIGHT; }
                else if (tone === 'danger') { bg = p.error.bg; fg = p.error.main; }
                else if (tone === 'info') { bg = p.info.bg; fg = p.primary.main; }
                else if (tone === 'purple') { bg = p.secondary.bg; fg = p.secondary.main; }
                return {
                    display: 'inline-flex', alignItems: 'center', gap: '5px', height: 24, p: '0 10px', borderRadius: '12px',
                    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', backgroundColor: bg, color: fg, lineHeight: 1.4,
                    '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' },
                    ...sx,
                };
            }}
        >
            {children}
        </Box>
    );
}

// `.btn` (+ .primary/.soft/.danger/.glass/.white, .sm, .block) — botões do kit
export function KitButton({ variant, size, block, children, sx, startIcon, ...rest }) {
    return (
        <Box
            component="button"
            type="button"
            {...rest}
            sx={(theme) => {
                const p = theme.palette;
                const v = {
                    default: { backgroundColor: p.background.paper, border: '1px solid', borderColor: p.divider, color: p.text.primary },
                    primary: { backgroundImage: p.gradient.brand, color: '#fff', border: 0, boxShadow: '0 6px 16px rgba(37,99,235,.28)' },
                    soft: { backgroundColor: p.info.bg, color: p.primary.main, border: '1px solid transparent' },
                    danger: { backgroundColor: p.error.bg, color: p.error.main, border: '1px solid transparent' },
                    success: { backgroundColor: p.success.main, color: '#fff', border: 0 },
                    white: { backgroundColor: '#fff', color: '#2563eb', border: 0 },
                    glass: { backgroundColor: 'rgba(255,255,255,.18)', color: '#fff', border: 0 },
                    ghost: { backgroundColor: 'transparent', color: p.primary.main, border: '1px solid transparent' },
                }[variant || 'default'];
                const sm = size === 'sm';
                return {
                    height: sm ? 36 : 48,
                    minHeight: sm ? 36 : 48,
                    borderRadius: sm ? '11px' : '14px',
                    p: sm ? '0 14px' : '0 18px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    fontWeight: 700, fontSize: sm ? 13 : 14.5, whiteSpace: 'nowrap', fontFamily: 'inherit', cursor: 'pointer',
                    width: block ? '100%' : undefined,
                    boxSizing: 'border-box', textDecoration: 'none', lineHeight: 1.4,
                    '&:active': { transform: 'scale(.98)' },
                    '&:disabled': { opacity: 0.6, cursor: 'default' },
                    ...v,
                    ...sx,
                };
            }}
        >
            {startIcon}
            {children}
        </Box>
    );
}
